/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ComponentProps, type ComponentType, lazy } from 'react';
import { Assert } from '@team/assert';

type Params<T> = {
  loader: () => Promise<T>;
};

/**
 * Компонент с отложенной загрузкой. Замена `React.lazy`
 *
 * Главное отличие - есть возможность сделать prefetch компонента,
 * после чего он начинает вести себя как обычный синхронный компонент
 *
 * Типизация через `any` используется самим реактом
 */
export const createLazyComponent = <T extends ComponentType<any>>({
  loader,
}: Params<T>) => {
  let ComponentCache = undefined as Maybe<T>;

  const LazyComponent = lazy(() =>
    loader().then((module) => ({ default: module }))
  );

  const Component = (props: ComponentProps<T>) => {
    if (Assert.isDefined(ComponentCache)) {
      return <ComponentCache {...props} />;
    }

    return <LazyComponent {...props} />;
  };

  const prefetch = async () => {
    if (Assert.isDefined(ComponentCache)) {
      return;
    }

    ComponentCache = await loader();
  };

  return {
    prefetch,
    Component,
  };
};
